import React, { useState, useEffect, useMemo, useCallback } from 'react';

import File from '@/components/Icons/File';
import { Input, Select, TextArea } from '@/components/DesignLibrary/Atoms';
import Button from '@/components/DesignLibrary/Button';
import type { Questionnaire } from '@/typings/Questionnaire';
import slugify from '@/helpers/slugify';
import TabText from '@/components/DesignLibrary/Atoms/TabText';
import MinusCircle from '@components/Icons/MinusCircle';
import Buttons from '@components/Buttons';
import Modal from '@/components/Modal';
import Spacer from '@/components/Spacer';
import { Reorder, useDragControls } from 'framer-motion';
import DotsSixVertical from '@components/Icons/DotsSixVertical';

import makeDefaultExpression from '@/helpers/makeDefaultExpression';
import { useFormEditorContext } from '@components/Questionnaire/questionnaireEditor/FormEditor/FormEditorContext';
import Conditions from '../Conditions';
import s from './SectionEditor.module.scss';

type SectionEditorProps = {
  section: Questionnaire.Section;
  usedSlugs: string[];
  usedKeys: string[];
  onUpdateSection: (updatedSection: Questionnaire.Section) => void;
  onRemoveSection: (sectionId: string) => void;
  isSaving: boolean;
};

type Expression = Pick<Questionnaire.Expression,
    | 'expression'
    | 'filterExpression'
    | 'filterExpressionVariableName'
    | 'filterExpressionOperand'
    | 'filterExpressionItemSelector'
    | 'filterExpressionReducer'
  > & {
  variables: Pick<Questionnaire.ExpressionVariable, 'variableName' | 'referenceId' | 'referenceType' | 'selector'>[];
};

const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  usedSlugs,
  usedKeys,
  onUpdateSection,
  onRemoveSection,
  isSaving,
}) => {
  const { pdfTemplates } = useFormEditorContext();
  const [label, setLabel] = useState<string>(section.label);
  const [selectedTab, setSelectedTab] = useState('settings');
  const [subsectionModalOpen, setSubsectionModalOpen] = useState(false);
  const [key, setKey] = useState<string>(section.key);
  const [description, setDescription] = useState<string>(section.description);
  const [subsections, setSubsections] = useState(section.subsections || []);
  const [newSubsectionTitle, setNewSubsectionTitle] = useState('');
  const [pdfId, setPdfId] = useState(section.pdfTemplateId ?? '');
  const [conditionExpression, setConditionExpression] = useState<Expression>(
    section.conditionExpression || makeDefaultExpression(),
  );

  const isValidKey = useMemo(() => !usedKeys.includes(key), [usedKeys, key]);

  const dragControls = useDragControls();

  useEffect(() => {
    setLabel(section.label);
    setKey(section.key);
    setDescription(section.description);
    setConditionExpression(section.conditionExpression || makeDefaultExpression());
    setPdfId(section.pdfTemplateId ?? '');
  }, [section]);

  const handleSave = useCallback((): void => {
    if (label && key && isValidKey) {
      onUpdateSection({
        ...section,
        conditionExpression: conditionExpression.expression ? conditionExpression : null,
        description,
        key,
        label,
        pdfTemplateId: pdfId || null,
        slug: slugify.generateUniqSlug({
          existingSlugs: usedSlugs,
          name: label,
        }),
        subsections,
      });
    }
  }, [section, key, isValidKey, label, usedSlugs, description, onUpdateSection, conditionExpression, subsections, pdfId]);

  const tabs = useMemo(
    () => [
      { key: 'settings', label: 'Settings', selected: selectedTab === 'settings' },
      { key: 'subsections', label: 'Subsections', selected: selectedTab === 'subsections' },
      { key: 'conditions', label: 'Conditions', selected: selectedTab === 'conditions' },
    ],
    [selectedTab],
  );

  const keyError: string | undefined = useMemo(() => {
    if (!key) {
      return 'Required';
    }

    if (!isValidKey) {
      return 'Key already used';
    }

    return undefined;
  }, [key, isValidKey]);

  return (
    <div className={s.sectionEditor}>
      <div className={s.sectionHeader}>
        <File />
        <span className={s.label}>Section Details</span>
      </div>
      <div className={s.sectionContent}>
        <div className={s.sectionContentTabs}>
          {tabs.map(tab => (
            <TabText {...tab} key={tab.key} onClick={() => setSelectedTab(tab.key)} />
          ))}
        </div>
        {selectedTab === 'settings' && (
          <>
            <Input label="" description="Section title" value={label} onChange={value => setLabel(value)} optional />
            <Input label="" description="Section key" value={key} error={keyError} onChange={value => setKey(value)} optional />
            <TextArea label="" description="Section description" value={description} onChange={setDescription} optional />
            <Select
              label='PDF'
              compact
              placeholder='Select PDF'
              options={[
                {
                  key: '',
                  label: 'None',
                },
                ...pdfTemplates.map((pdfTemplate) => ({ key: pdfTemplate.id, label: pdfTemplate.name })),
              ]}
              value={pdfId}
              onChange={id => setPdfId(pdfTemplates.find((p) => p.id === id)?.id ?? '')}
            />
          </>
        )}
        {selectedTab === 'conditions' && <>
          <Conditions setConditionExpression={setConditionExpression} conditionExpression={conditionExpression} />
          <Button loading={isSaving} variant='primary' size='sm' onClick={handleSave}>
            Save
          </Button>
        </>}
        {selectedTab === 'settings' && (
          <>
            <Button loading={isSaving} variant="primary" size="sm" onClick={handleSave}>
              Save
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this section?')) {
                  onRemoveSection(section.id);
                }
              }}
            >
              Delete section
            </Button>
          </>
        )}
        {selectedTab === 'subsections' && (
          <>
            <div>
              <Reorder.Group className={s.reorder} axis='y' values={subsections} onReorder={setSubsections}>
                {subsections.map((subsection) => (
                  <Reorder.Item
                    value={subsection}
                    as='div'
                    className={s.subsectionPill}
                    key={subsection}
                    whileDrag={{ backgroundColor: 'white', boxShadow: 'var(--shadow-lg)', cursor: 'grabbing' }}
                    dragControls={dragControls}
                  >
                    <div className={s.left}>
                      <DotsSixVertical onPointerDown={(e) => dragControls.start(e)} />
                      <p className='body-sm'>{subsection}</p>
                    </div>
                    <Buttons
                      isWrapper
                      className={s.deleteButton}
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this subsection?')) {
                          setSubsections(prev => prev.filter((sub) => sub !== subsection));
                        }
                      }}
                    >
                      <MinusCircle size={16} />
                    </Buttons>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            </div>
            <Button size='sm' variant='secondary' onClick={() => setSubsectionModalOpen(true)}>
              Add subsection
            </Button>
            {subsectionModalOpen && (
              <Modal title="Add subsection" onClose={() => setSubsectionModalOpen(false)} hasPadding>
                <Input
                  label=""
                  description="Subsection title"
                  value={newSubsectionTitle}
                  onChange={setNewSubsectionTitle}
                  optional
                />
                <Spacer size={16} />
                <Button
                  variant='primary'
                  size='md'
                  disabled={!newSubsectionTitle || subsections.includes(newSubsectionTitle)}
                  onClick={() => {
                    setSubsections(prev => [...prev, newSubsectionTitle]);
                    setNewSubsectionTitle('');
                    setSubsectionModalOpen(false);
                  }}
                  fullWidth
                >
                  Add subsection
                </Button>
              </Modal>
            )}
            <Button loading={isSaving} variant='primary' size='sm' onClick={handleSave}>
              Save
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SectionEditor;
